import type {
  InviteEligibilityApplicant,
  InviteEligibilityOutcome,
  InviteEligibilityResponseData,
} from '@/modules/application-tracker/api';

/** Create invite or edit invite; both share the eligibility controller. */
export type InviteEligibilityFlow = 'create' | 'edit';

/**
 * What the form showed when Send was clicked. `unavailable`: the check failed and no banner
 * was shown (fail open). `pending`: the check was still waiting or loading. `not_checked`: the
 * flag was off or the form never had enough input for a check.
 */
export type InviteEligibilityPreflightOutcome =
  InviteEligibilityOutcome | 'unavailable' | 'pending' | 'not_checked';

/** What the backend decided on submit. Only a 409 eligibility block carries a decision. */
export type InviteEligibilityFinalOutcome = 'block' | 'not_blocked';

export type InviteSubmitErrorType =
  'eligibility_block' | 'validation' | 'conflict' | 'permission' | 'server' | 'network' | 'other';

export type InviteEligibilityLatencyBucket =
  'under_250ms' | '250ms_to_500ms' | '500ms_to_1s' | '1s_to_2s' | '2s_to_5s' | 'over_5s';

/**
 * Check result without renter data. Emails, record ids and addresses stay out of analytics
 * and RUM.
 */
export type InviteEligibilityCheckSummary = {
  outcome: InviteEligibilityOutcome;
  reason: string[];
  applicant_count: number;
  match_count: number;
  source_systems: string[];
};

export type InviteEligibilityRowProperties = {
  reason: string;
  applicant_index: number;
  match_count: number;
};

const distinctSorted = (values: Array<string | undefined>): string[] =>
  [...new Set(values.filter((value): value is string => Boolean(value)))].sort((a, b) =>
    a.localeCompare(b),
  );

export const summarizeInviteEligibilityCheck = (
  data: InviteEligibilityResponseData,
): InviteEligibilityCheckSummary => {
  const applicants = data.applicants ?? [];
  return {
    outcome: data.outcome,
    reason: distinctSorted(applicants.map((applicant) => applicant.reason)),
    applicant_count: applicants.length,
    match_count: applicants.reduce((total, applicant) => total + (applicant.total_matches ?? 0), 0),
    source_systems: distinctSorted(
      applicants.flatMap((applicant) => (applicant.matches ?? []).map((match) => match.source)),
    ),
  };
};

export const inviteEligibilityRowProperties = (
  applicant: InviteEligibilityApplicant,
  applicantIndex: number,
): InviteEligibilityRowProperties => ({
  reason: applicant.reason,
  applicant_index: applicantIndex,
  match_count: applicant.total_matches ?? 0,
});

export const inviteEligibilityLatencyBucket = (
  durationMs: number,
): InviteEligibilityLatencyBucket => {
  if (durationMs < 250) {
    return 'under_250ms';
  }
  if (durationMs < 500) {
    return '250ms_to_500ms';
  }
  if (durationMs < 1000) {
    return '500ms_to_1s';
  }
  if (durationMs < 2000) {
    return '1s_to_2s';
  }
  if (durationMs < 5000) {
    return '2s_to_5s';
  }
  return 'over_5s';
};

export const inviteEligibilityFinalOutcome = (
  eligibilityBlocked: boolean,
): InviteEligibilityFinalOutcome => (eligibilityBlocked ? 'block' : 'not_blocked');

export const inviteSubmitErrorType = ({
  status,
  eligibilityBlocked,
  hasFieldErrors,
}: {
  status: number | undefined;
  eligibilityBlocked: boolean;
  hasFieldErrors: boolean;
}): InviteSubmitErrorType => {
  if (eligibilityBlocked) {
    return 'eligibility_block';
  }
  if (status === undefined) {
    return 'network';
  }
  if (status >= 500) {
    return 'server';
  }
  if (status === 401 || status === 403) {
    return 'permission';
  }
  if (status === 409) {
    return 'conflict';
  }
  if (hasFieldErrors || status === 400 || status === 422) {
    return 'validation';
  }
  return 'other';
};

// Written by the eligibility controller on every render and read once when Send is clicked,
// so the submit event carries what the landlord was looking at.
const preflightOutcomeByFlow: Record<InviteEligibilityFlow, InviteEligibilityPreflightOutcome> = {
  create: 'not_checked',
  edit: 'not_checked',
};

export const setInviteEligibilityPreflightOutcome = (
  flow: InviteEligibilityFlow,
  outcome: InviteEligibilityPreflightOutcome,
) => {
  preflightOutcomeByFlow[flow] = outcome;
};

export const getInviteEligibilityPreflightOutcome = (
  flow: InviteEligibilityFlow,
): InviteEligibilityPreflightOutcome => preflightOutcomeByFlow[flow];
